/**
 * Handlers d'événements pour le ChatFlowDiagram
 */
import { getAccessToken } from '../../../../Services/GraphServices';
import { UpdateChatFlow, GetChatFlow } from '../../../../Services/HeedifyServices';
import { parseEdgeId } from './nodeUtils';

/**
 * Crée les handlers pour la gestion des edges
 */
export const createEdgeHandlers = ({
  chatFlowData,
  setChatFlowData,
  setEdges,
  setNodes,
  saveChatFlowToDatabase,
  instance,
  activeAccount,
  ACTIVE_TENANT,
  userId,
  chatFlowId
}) => {
  
  /**
   * Suppression interne d'un edge (après confirmation)
   */
  const handleEdgeDeleteInternal = (edgeId) => {
    
    // Empêcher la suppression de l'edge greeting -> routing-0
    if (edgeId === 'e-greeting-routing-0' || edgeId === 'e-chatflow-start-routing-0') {
      console.warn('⚠️ Impossible de supprimer l\'edge vers le routing de démarrage');
      return;
    }
    
    const { sourceKey, targetKey, actionKey, hasActionKey } = parseEdgeId(edgeId);
    
    const routing = chatFlowData?.routing?.[sourceKey];
    
    if (routing) {
      let updatedChatFlowData = null;
      
      // Cas 1: Customer Choice - supprimer l'action spécifique
      if ((routing.type?.toLowerCase() === 'customer choice' || routing.type?.toLowerCase() === 'customerchoice') 
          && routing.customerChoiceActions && actionKey) {
        
        const updatedActions = { ...routing.customerChoiceActions };
        delete updatedActions[actionKey];
        
        updatedChatFlowData = {
          ...chatFlowData,
          routing: {
            ...chatFlowData.routing,
            [sourceKey]: {
              ...routing,
              customerChoiceActions: updatedActions
            }
          }
        };
        
      }
      // Cas 2: Form ou Queue - supprimer le next
      else if ((routing.type?.toLowerCase() === 'form' || routing.type?.toLowerCase() === 'queue') && routing.next) {
        
        updatedChatFlowData = {
          ...chatFlowData,
          routing: {
            ...chatFlowData.routing,
            [sourceKey]: {
              ...routing,
              next: undefined
            }
          }
        };
        
      }
      
      // Sauvegarder si des modifications ont été faites
      if (updatedChatFlowData) {
        
        saveChatFlowToDatabase(updatedChatFlowData)
          .then(() => {
            setEdges([]);
            setNodes([]);
            return getAccessToken(instance, activeAccount);
          })
          .then((token) => {
            return GetChatFlow(ACTIVE_TENANT, userId, chatFlowId, 'default', token);
          })
          .then((data) => {
            setChatFlowData(data);
          })
          .catch((error) => {
            console.error('❌ Erreur lors de la sauvegarde:', error);
          });
        return;
      }
    }
    
    // Fallback: supprimer l'edge de la liste visuelle seulement
    setEdges((eds) => eds.filter((e) => e.id !== edgeId));
  };
  
  /**
   * Changement de label d'un edge (pour Customer Choice actions)
   */
  const handleEdgeLabelChange = (edgeId, newLabel) => {
    
    const parts = edgeId.split('-');
    
    if (parts[0] === 'e' && parts[1] === 'routing') {
      const routingKey = parts[2];
      const actionKey = parts[parts.length - 1];
      
      const routing = chatFlowData?.routing?.[routingKey];
      
      if (routing && (routing.type === 'Customer Choice' || routing.type === 'customerChoice' || routing.type?.toLowerCase() === 'customer choice')) {
        const action = routing.customerChoiceActions?.[actionKey];
        
        if (action) {
          const updatedChatFlowData = {
            ...chatFlowData,
            routing: {
              ...chatFlowData.routing,
              [routingKey]: {
                ...routing,
                customerChoiceActions: {
                  ...routing.customerChoiceActions,
                  [actionKey]: {
                    ...action,
                    text: newLabel,
                    displayText: newLabel,
                    title: newLabel
                  }
                }
              }
            }
          };
          
          setChatFlowData(updatedChatFlowData);
          
          saveChatFlowToDatabase(updatedChatFlowData)
            .then(() => {
            })
            .catch((error) => {
              console.error('❌ Erreur sauvegarde label:', error);
            });
        }
      }
    }
  };
  
  return {
    handleEdgeDeleteInternal,
    handleEdgeLabelChange
  };
};

/**
 * Crée les handlers pour la gestion des nœuds
 */
export const createNodeHandlers = ({
  chatFlowData,
  handleSaveNode
}) => {
  
  const handleDeleteNode = async (nodeId) => {
    if (nodeId === 'greeting') {
      const updatedChatFlowData = {
        ...chatFlowData,
        greeting: {}
      };
      await handleSaveNode(updatedChatFlowData);
    } else if (nodeId.startsWith('routing-')) {
      const routingKey = nodeId.replace('routing-', '');
      const updatedRouting = { ...chatFlowData.routing };
      delete updatedRouting[routingKey];
      
      // Nettoyer les références dans les customerChoiceActions et Form next
      Object.keys(updatedRouting).forEach(key => {
        const routing = updatedRouting[key];
        
        // Nettoyer les actions Customer Choice qui pointent vers ce routing
        if (routing.type?.toLowerCase() === 'customer choice' && routing.customerChoiceActions) {
          const updatedActions = { ...routing.customerChoiceActions };
          let hasChanges = false;
          
          Object.keys(updatedActions).forEach(actionKey => {
            const action = updatedActions[actionKey];
            if (action.next?.routingName === routingKey) {
              delete updatedActions[actionKey];
              hasChanges = true;
            }
          });
          
          if (hasChanges) {
            updatedRouting[key] = {
              ...routing,
              customerChoiceActions: updatedActions
            };
          }
        }
        
        // Nettoyer le next des Forms qui pointent vers ce routing
        if (routing.type?.toLowerCase() === 'form' && routing.next?.routingName === routingKey) {
          updatedRouting[key] = {
            ...routing,
            next: undefined
          };
        }
      });
      
      const updatedChatFlowData = {
        ...chatFlowData,
        routing: updatedRouting
      };
      await handleSaveNode(updatedChatFlowData);
    }
  };
  
  return {
    handleDeleteNode
  };
};

/**
 * Crée les handlers pour le drag & drop
 */
export const createDragDropHandlers = ({
  chatFlowData,
  Token,
  instance,
  activeAccount,
  ACTIVE_TENANT,
  userId,
  setChatFlowData
}) => {
  
  const onDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };
  
  const onDrop = async (event) => {
    event.preventDefault();
    const type = event.dataTransfer.getData('application/reactflow');
    
    if (!type) return;
    
    
    if (type === 'greeting') {
      const currentGreeting = chatFlowData.greeting || {};
      const updatedChatFlowData = {
        ...chatFlowData,
        greeting: {
          ...currentGreeting,
          welcomeMessage: {
            text: 'Hello, welcome to our chat support by Heedify',
            ...currentGreeting.welcomeMessage,
            enabled: true
          }
        }
      };
      
      try {
        const token = Token || (await getAccessToken(instance, activeAccount));
        const scope = updatedChatFlowData.scope || 'default';
        
        await UpdateChatFlow(
          ACTIVE_TENANT,
          userId,
          updatedChatFlowData.id,
          scope,
          token,
          updatedChatFlowData
        );
        
        setChatFlowData(updatedChatFlowData);
      } catch (error) {
        console.error('Erreur création greeting:', error);
        alert('Erreur lors de la création du greeting');
      }
    } else if (['queue', 'customer-choice', 'form'].includes(type.toLowerCase())) {
      const timestamp = Date.now();
      const routingKey = `${type.toLowerCase().replace(/\-/g, '_')}_${timestamp}`;
      
      let routingObject = {};
      
      if (type.toLowerCase() === 'queue') {
        routingObject = {
          enabled: true,
          type: 'queue',
          queueDisplayName: '',
          queueId: ''
        };
      } else if (type.toLowerCase() === 'customer-choice') {
        routingObject = {
          type: 'customerChoice',
          enabled: true,
          displayName: 'Customer Choice'
        };
      } else if (type.toLowerCase() === 'form') {
        routingObject = {
          type: 'form',
          enabled: true,
          displayName: 'Form'
        };
      }
      
      const updatedChatFlowData = {
        ...chatFlowData,
        routing: {
          ...chatFlowData.routing,
          [routingKey]: routingObject
        }
      };
      
      try {
        const token = Token || (await getAccessToken(instance, activeAccount));
        const scope = updatedChatFlowData.scope || 'default';
        
        await UpdateChatFlow(
          ACTIVE_TENANT,
          userId,
          updatedChatFlowData.id,
          scope,
          token,
          updatedChatFlowData
        );
        
        setChatFlowData(updatedChatFlowData);
      } catch (error) {
        console.error('Erreur création routing:', error);
        alert('Erreur lors de la création du routing');
      }
    }
  };
  
  return {
    onDragOver,
    onDrop
  };
};

/**
 * Crée le handler pour les nouvelles connexions
 */
export const createConnectionHandler = ({
  chatFlowData,
  setChatFlowData,
  saveChatFlowToDatabase
}) => {
  
  const onConnect = (connection) => {
    
    const { source, target } = connection;
    
    // Gérer les connexions depuis greeting vers un routing
    if (source === 'greeting' && target?.startsWith('routing-')) {
      const targetRoutingKey = target.replace('routing-', '');
      
      // Si le target n'est pas déjà "0", on doit faire un swap
      if (targetRoutingKey !== "0") {
        const currentRouting0 = chatFlowData.routing?.["0"];
        const targetRouting = chatFlowData.routing?.[targetRoutingKey];
        
        if (currentRouting0 && targetRouting) {
          // Générer un nouveau nom pour l'ancien routing "0"
          const timestamp = Date.now();
          const oldRouting0Type = currentRouting0.type?.toLowerCase() || 'queue';
          const newKeyForOldRouting0 = `${oldRouting0Type}_${timestamp}`;
          
          // Nettoyer le displayName de l'ancien routing "0" en enlevant " (start)" s'il existe
          let oldRouting0DisplayName = currentRouting0.displayName || '';
          if (oldRouting0DisplayName.includes(' (start)')) {
            oldRouting0DisplayName = oldRouting0DisplayName.replace(' (start)', '');
          }
          // Si le displayName est vide ou c'est juste "0" ou "start", utiliser le nouveau nom comme displayName
          if (!oldRouting0DisplayName || oldRouting0DisplayName === '0' || oldRouting0DisplayName === 'start') {
            oldRouting0DisplayName = newKeyForOldRouting0;
          }
          
          const targetRoutingDisplayName = targetRouting.displayName || targetRoutingKey;
          
          
          // Créer la nouvelle structure routing
          const newRouting = { ...chatFlowData.routing };
          
          // Renommer l'ancien routing "0" avec son displayName actuel
          newRouting[newKeyForOldRouting0] = {
            ...currentRouting0,
            displayName: oldRouting0DisplayName
          };
          delete newRouting["0"];
          
          // Le target devient le nouveau routing "0" 
          // On met à jour le displayName pour indiquer que c'est le start
          newRouting["0"] = {
            ...targetRouting,
            displayName: `${targetRoutingDisplayName} (start)`
          };
          delete newRouting[targetRoutingKey];
          
          // Mettre à jour toutes les références vers l'ancien routing "0" et targetRoutingKey
          Object.keys(newRouting).forEach(key => {
            const routing = newRouting[key];
            
            // Mettre à jour les références dans customerChoiceActions
            if (routing.customerChoiceActions) {
              Object.keys(routing.customerChoiceActions).forEach(actionKey => {
                const action = routing.customerChoiceActions[actionKey];
                if (action.next?.routingName === "0") {
                  action.next.routingName = newKeyForOldRouting0;
                } else if (action.next?.routingName === targetRoutingKey) {
                  action.next.routingName = "0";
                }
              });
            }
            
            // Mettre à jour les références dans next (Form/Queue)
            if (routing.next?.routingName === "0") {
              routing.next.routingName = newKeyForOldRouting0;
            } else if (routing.next?.routingName === targetRoutingKey) {
              routing.next.routingName = "0";
            }
          });
          
          const updatedChatFlowData = {
            ...chatFlowData,
            routing: newRouting,
            greeting: {
              ...(chatFlowData.greeting || {}),
              nextRouting: undefined // Greeting pointe maintenant vers le nouveau routing "0" par défaut
            }
          };
          
          setChatFlowData(updatedChatFlowData);
          
          saveChatFlowToDatabase(updatedChatFlowData)
            .catch((error) => console.error('❌ Erreur sauvegarde:', error));
          
          return;
        }
      }
      
      // Si c'est déjà vers routing "0", juste réinitialiser nextRouting
      const updatedChatFlowData = {
        ...chatFlowData,
        greeting: {
          ...(chatFlowData.greeting || {}),
          nextRouting: undefined
        }
      };
      
      setChatFlowData(updatedChatFlowData);
      
      saveChatFlowToDatabase(updatedChatFlowData)
        .catch((error) => console.error('❌ Erreur sauvegarde:', error));
      
      return;
    }
    
    if (source?.startsWith('routing-')) {
      const routingKey = source.replace('routing-', '');
      const routing = chatFlowData?.routing?.[routingKey];
      
      if (routing && (routing.type?.toLowerCase() === 'customer choice' || routing.type === 'Customer Choice' || routing.type === 'customerChoice')) {
        // Créer une nouvelle action Customer Choice
        const newActionId = Date.now();
        const newActionName = `${Object.keys(routing.customerChoiceActions || {}).length + 1}`;
        
        let nextObject = undefined;
        
        if (target?.startsWith('routing-')) {
          const targetRoutingKey = target.replace('routing-', '');
          nextObject = {
            type: 'routing',
            routingName: targetRoutingKey
          };
        }
        
        const newAction = {
          text: newActionName,
          displayText: newActionName,
          title: newActionName,
          value: newActionId,
          type: 'postBack',
          next: nextObject
        };
        
        const updatedChatFlowData = {
          ...chatFlowData,
          routing: {
            ...chatFlowData.routing,
            [routingKey]: {
              ...routing,
              customerChoiceActions: {
                ...(routing.customerChoiceActions || {}),
                [newActionId]: newAction
              }
            }
          }
        };
        
        setChatFlowData(updatedChatFlowData);
        
        saveChatFlowToDatabase(updatedChatFlowData)
          .catch((error) => console.error('❌ Erreur sauvegarde:', error));
        
        return;
      }
      
      // Gérer next pour Form, Queue et autres routings
      if (routing && ['form', 'queue'].includes(routing.type?.toLowerCase())) {
        let nextObject = undefined;
        
        if (target?.startsWith('routing-')) {
          const targetRoutingKey = target.replace('routing-', '');
          const targetRouting = chatFlowData?.routing?.[targetRoutingKey];
          
          if (targetRouting) {
            const targetType = targetRouting.type?.toLowerCase();
            
            if (targetType === 'queue') {
              nextObject = { type: 'queue', routingName: targetRoutingKey };
            } else if (targetType === 'customerchoice' || targetType === 'customer choice') {
              nextObject = { type: 'customerChoice', routingName: targetRoutingKey };
            } else if (targetType === 'form') {
              nextObject = { type: 'form', routingName: targetRoutingKey };
            } else {
              nextObject = { type: targetType, routingName: targetRoutingKey };
            }
          }
        }
        
        const updatedChatFlowData = {
          ...chatFlowData,
          routing: {
            ...chatFlowData.routing,
            [routingKey]: {
              ...routing,
              next: nextObject
            }
          }
        };
        
        setChatFlowData(updatedChatFlowData);
        
        saveChatFlowToDatabase(updatedChatFlowData)
          .catch((error) => console.error('❌ Erreur sauvegarde:', error));
        
        return;
      }
    }
  };
  
  const isValidConnection = (connection) => {
    if (connection.target?.startsWith('routing-')) {
      return true;
    }
    return true;
  };
  
  return {
    onConnect,
    isValidConnection
  };
};
